import { OrderCardHeader } from '@src/components/OMS/OMSCard/OrderCardHeader';
import { OrderCardSummary } from '@src/components/OMS/OMSCard/OrderCardSummary';
import { useOrderInteractions } from '@src/providers';
import {
  ACTION,
  Dialog,
  Divider,
  HStack,
  MixpanelEvent,
  MixpanelEventProperty,
  OrdTypeEnum,
  Text,
  VStack,
  abbreviateId,
  isOrderComplete,
  isOrderPaused,
  useMixpanel,
  useParameterRows,
  useSecurity,
  useStrategiesContext,
  useUserContext,
  type DialogProps,
  type ExecutionReport,
  type Order,
} from '@talos/kyoko';
import { useCallback, useEffect } from 'react';
import { hideFeedbackCard } from '../../../../components/OMS/OMSSlice';
import { useRoleAuth } from '../../../../hooks';
import { useMaxOrderSizeDialogs } from '../../../../hooks/useMaxOrderSizeDialogs';
import { useAppStateDispatch } from '../../../../providers/AppStateProvider';
import { SelectedOrdersList } from './SelectedOrdersList';
import { SelectedOrdersUsers } from './SelectedOrdersUsers';
import { ResumeDialogWrapper } from './styles';
import { useBulkResumeValidationState } from './useBulkResumeValidationState';

interface BulkResumeDialogProps {
  selectedOrders?: Order[];
  resumeDialog: DialogProps;
}

export const BulkResumeDialog = ({ selectedOrders, resumeDialog }: BulkResumeDialogProps) => {
  if (!selectedOrders || selectedOrders.length === 0) {
    return null;
  }

  if (selectedOrders.length === 1) {
    return <ResumeDialog resumeDialog={resumeDialog} selectedOrder={selectedOrders[0]} />;
  }

  return <ResumeMultipleDialog resumeDialog={resumeDialog} selectedOrders={selectedOrders} />;
};

interface ResumeDialogProps {
  selectedOrder?: Order | ExecutionReport;
  resumeDialog: DialogProps;
}

export const ResumeDialog = ({ selectedOrder, resumeDialog }: ResumeDialogProps) => {
  const { isAuthorized } = useRoleAuth();
  const mixpanel = useMixpanel();
  const orders = useOrderInteractions();
  const security = useSecurity(selectedOrder?.Symbol);
  const { strategiesByName: strats } = useStrategiesContext();
  const paramRows = useParameterRows(selectedOrder?.Strategy, selectedOrder?.Parameters);
  const dispatch = useAppStateDispatch();

  const handleResume = useCallback(() => {
    dispatch(hideFeedbackCard());
    if (selectedOrder) {
      mixpanel.track(MixpanelEvent.ResumeOrder, { [MixpanelEventProperty.ID]: selectedOrder.OrderID });
      orders.resume(selectedOrder.OrderID);
    }
  }, [dispatch, selectedOrder, mixpanel, orders]);

  const { warnMessage, rejectMessage, validationState, validate } = useMaxOrderSizeDialogs({
    currency: selectedOrder == null ? undefined : (selectedOrder.Currency ?? ''),
    symbol: selectedOrder?.Symbol,
    security,
    onConfirmWarning: handleResume,
  });

  useEffect(() => {
    if (resumeDialog.isOpen && selectedOrder != null) {
      validate({ size: selectedOrder.OrderQty });
    }
  }, [resumeDialog.isOpen, selectedOrder, validate]);

  if (selectedOrder == null || security == null) {
    return null;
  }

  const paused = isOrderPaused(selectedOrder.DecisionStatus);
  const showFees = selectedOrder.OrdType === OrdTypeEnum.LimitAllIn;
  const selectedStrategy = selectedOrder.Strategy ? strats?.get(selectedOrder.Strategy) : undefined;

  const showLimitMessage = validationState === 'warn' || validationState === 'reject' || validationState === 'loading';
  const confirmLabel =
    validationState === 'loading'
      ? 'Resume without limit check'
      : validationState === 'warn'
        ? 'Resume anyway'
        : 'Resume Order';

  return (
    <Dialog
      {...resumeDialog}
      width={320}
      onConfirm={handleResume}
      confirmLabel={confirmLabel}
      cancelLabel="Return"
      confirmDisabled={
        !paused || !isAuthorized(ACTION.RESUME_ORDER) || validationState === 'reject' || validationState === 'idle'
      }
      title="Confirm Resumption"
      stretchButtons
    >
      <ResumeDialogWrapper>
        <VStack gap="spacing16" alignItems="stretch">
          <h3 style={{ textAlign: 'left' }}>
            Order #<b>{abbreviateId(selectedOrder.OrderID)}</b>
          </h3>
          <Text style={{ textAlign: 'left' }} color="colorTextAttention">
            Are you sure you want to resume this order?
          </Text>
          {showLimitMessage && (
            <>
              <Divider />
              {validationState === 'loading' && (
                <Text color="colorTextSubtle">
                  Order size limits cannot be evaluated right now. Resuming will bypass this client-side check.
                </Text>
              )}
              {validationState === 'warn' && (
                <Text as="div" color="colorTextWarning">
                  {warnMessage}
                </Text>
              )}
              {validationState === 'reject' && (
                <Text as="div" color="colorTextNegative">
                  {rejectMessage}
                </Text>
              )}
            </>
          )}
          <Divider />
          <VStack gap="spacing8" alignItems="stretch">
            <OrderCardHeader order={selectedOrder} selectedStrategy={selectedStrategy} />
            <Divider />
            <OrderCardSummary security={security} showFees={showFees} {...selectedOrder} customerOrder={undefined} />
          </VStack>
          {paramRows.length > 0 && (
            <>
              <Divider />
              <VStack gap="spacing8" alignItems="stretch">
                {paramRows.map(row => {
                  return (
                    <HStack justifyContent="space-between" key={row.title}>
                      <Text color="colorTextAttention">{row.title}</Text>
                      <Text>{row.value}</Text>
                    </HStack>
                  );
                })}
              </VStack>
            </>
          )}
        </VStack>
      </ResumeDialogWrapper>
    </Dialog>
  );
};

const ResumeMultipleDialog = ({ selectedOrders, resumeDialog }: Required<BulkResumeDialogProps>) => {
  const { isAuthorized } = useRoleAuth();
  const mixpanel = useMixpanel();
  const { resume } = useOrderInteractions();
  const { user } = useUserContext();
  const {
    confirmDisabledForValidation,
    confirmLabel,
    hasReject,
    hasUnavailableValidation,
    hasWarn,
    isValidationPending,
    onValidationResult,
    orderStatuses,
    pendingCount,
    rejectedCount,
    showLimitMessage,
    unavailableCount,
    warnCount,
  } = useBulkResumeValidationState({ isOpen: resumeDialog.isOpen, selectedOrders });

  const onHandleResume = useCallback(
    () =>
      selectedOrders.forEach(selectedOrder => {
        mixpanel.track(MixpanelEvent.ResumeOrder, { [MixpanelEventProperty.ID]: selectedOrder.OrderID });
        resume(selectedOrder.OrderID);
      }),
    [selectedOrders, mixpanel, resume]
  );

  return (
    <Dialog
      {...resumeDialog}
      onConfirm={onHandleResume}
      confirmLabel={confirmLabel}
      cancelLabel="Return"
      confirmDisabled={
        selectedOrders.every(selectedOrder => isOrderComplete(selectedOrder.OrdStatus)) ||
        !isAuthorized(ACTION.RESUME_ORDER) ||
        confirmDisabledForValidation
      }
      width={420}
      title="Confirm Resumption"
    >
      <ResumeDialogWrapper>
        <VStack gap="spacing8" alignItems="stretch">
          {selectedOrders.every(selectedOrder => selectedOrder.User === user?.Name) ? (
            <Text style={{ textAlign: 'left' }} color="colorTextAttention">
              Are you sure you want to resume these {selectedOrders.length} orders?
            </Text>
          ) : (
            <Text style={{ textAlign: 'left' }} color="colorTextAttention">
              You are about to resume {selectedOrders.length} orders belonging to{' '}
              <SelectedOrdersUsers selectedOrders={selectedOrders} />
              .<br />
              Are you sure you want to proceed?
            </Text>
          )}
          {showLimitMessage && (
            <>
              <Divider />
              {hasReject && (
                <Text color="colorTextNegative">
                  {rejectedCount} {rejectedCount === 1 ? 'order exceeds' : 'orders exceed'} the rejection limit and
                  cannot be resumed.
                </Text>
              )}
              {!hasReject && hasWarn && (
                <Text color="colorTextWarning">
                  {warnCount} {warnCount === 1 ? 'order exceeds' : 'orders exceed'} the warning threshold.
                </Text>
              )}
              {!hasReject && !hasWarn && isValidationPending && (
                <Text color="colorTextSubtle">
                  Evaluating order size limits for {pendingCount} {pendingCount === 1 ? 'order' : 'orders'}.
                </Text>
              )}
              {!hasReject && !hasWarn && !isValidationPending && hasUnavailableValidation && (
                <Text color="colorTextSubtle">
                  Order size limits cannot be evaluated for {unavailableCount}{' '}
                  {unavailableCount === 1 ? 'order' : 'orders'}. Resuming will bypass this client-side check for{' '}
                  {unavailableCount === 1 ? 'that order' : 'those orders'}.
                </Text>
              )}
            </>
          )}
          <Divider />
          <SelectedOrdersList
            selectedOrders={selectedOrders}
            isDialogOpen={resumeDialog.isOpen}
            onValidationResult={onValidationResult}
            orderStatuses={orderStatuses}
          />
        </VStack>
      </ResumeDialogWrapper>
    </Dialog>
  );
};
